import axios from 'axios';
import {useLanguageData} from "../App";
import {useDataContext} from "../views/SurveyContextProvider";
import {useIntl} from "react-intl";
import {getAuthConfig} from "../utils";
import _ from "lodash";
const auth = getAuthConfig()

export const processSingleBotInstructions = ({
                                                 bot,
                                                 processedConfig,
                                                 intl,
                                                 categoryData = null,
                                             }) => {

    let supportTopicInstructions = '';

    const topicDataSource = categoryData || bot.config?.topicData || {};

    if (Array.isArray(bot.config?.supportTopics)) {
        supportTopicInstructions = bot.config.supportTopics
            .map(topicCode => {
                const topic = topicDataSource[topicCode];

                if (topic) {
                    let topicInstruction = processedConfig.supportTopicInstructionBody || '';
                    const botVersion = bot.config?.version;

                    const instructions = (botVersion && topic?.versions?.[botVersion]?.instructions) ?
                        topic.versions[botVersion].instructions :
                        topic.instructions || '';

                    const requestType = (botVersion && topic?.versions?.[botVersion]?.requesttype) ?
                        topic.versions[botVersion].requesttype :
                        topic.requesttype || '';

                    const followupType = (botVersion && topic?.versions?.[botVersion]?.followuptype) ?
                        topic.versions[botVersion].followuptype :
                        topic.followuptype || '';

                    topicInstruction = topicInstruction
                        .replace(/{supportTopicTitle}/g, topic.title || '')
                        .replace(/{supportTopicCode}/g, topicCode || '')
                        .replace(/{supportRequestTarget}/g, bot.config?.supportRequestTarget || '')
                        .replace(/{instructions}/g, instructions)
                        .replace(/{requesttypeTranslation}/g, requestType ? intl.formatMessage({
                            defaultMessage: requestType,
                            id: `bot.requestType.${requestType}`
                        }) : '')
                        .replace(/{followuptype}/g, followupType);

                    return topicInstruction;
                }
                return '';
            })
            .filter(i => i)
            .join('\n\n');
    }

    let formattedMaterials = '';
    if (Array.isArray(bot.config?.materials) && bot.config.materials.length > 0) {
        formattedMaterials = bot.config.materials.map(material => `- ${material}`).join('\n');
    }

    let botInstructionSection = processedConfig.supportBotInstructionBody || '';
    botInstructionSection = botInstructionSection
        .replace(/{supportRequestTargetTranslation}/g, bot.config?.supportRequestTarget ? intl.formatMessage({
            defaultMessage: bot.config?.supportRequestTarget,
            id: `role.${bot.config?.supportRequestTarget}`
        }) : '')
        .replace(/{generalInstructions}/g, bot.config?.generalInstructions || '')
        .replace(/{materials}/g, formattedMaterials)
        .replace(/{supportTopicInstructions}/g, supportTopicInstructions);

    return botInstructionSection;
};

// Updated processAssistantInstructions to use the shared function
export const processAssistantInstructions = ({
                                                 assistantInstructionsConfig,
                                                 surveyData,
                                                 supportBots,
                                                 categoryData,
                                                 supportBotInstructions,
                                                 supportTopicInstructionBody,
                                                 supportBotInstructionBody,
                                                 moduleInstructionsLang,
                                                 intl,
                                                 surveyId,
                                                 isTest = false
                                             }) => {
    const processedConfig = replaceInstructionVariables(
        assistantInstructionsConfig,
        supportBotInstructions,
        supportTopicInstructionBody,
        supportBotInstructionBody,
        moduleInstructionsLang
    );

    if (processedConfig && typeof processedConfig === 'object' && processedConfig?.postBody) {
        // Set the assistant name with optional TEST prefix
        if (surveyData?.config?.title && surveyId) {
            const prefix = isTest ? '[TEST] ' : '';
            processedConfig.postBody.name = `${prefix}${surveyData.config.title} (${surveyId})`;
        }

        if (surveyData?.config?.useCustomInstructions && surveyData?.config?.customInstructions) {
            processedConfig.postBody.instructions = surveyData.config.customInstructions;
        } else {
            let allBotInstructions = '';

            if (Array.isArray(surveyData?.config?.supportBots) && supportBots) {
                surveyData.config.supportBots.forEach(botId => {
                    const bot = supportBots.find(b => b.id === botId);

                    if (bot) {
                        const botInstructionSection = processSingleBotInstructions({
                            bot,
                            processedConfig,
                            intl,
                            categoryData
                        });

                        allBotInstructions += botInstructionSection + '\n\n';
                    }
                });
            }

            if (processedConfig.postBody.instructions && typeof processedConfig.postBody.instructions === 'string') {
                processedConfig.postBody.instructions = processedConfig.postBody.instructions
                    .replace(/{botInstructions}/g, allBotInstructions.trim())
                    .replace(/{initialMessage}/g, `${surveyData?.config?.message || ''}`);
            }
        }
    }

    return processedConfig?.postBody;
};

export function replaceInstructionVariables(
    assistantInstructionsConfig,
    supportBotInstructions,
    supportTopicInstructionBody,
    supportBotInstructionBody,
    language = "en"
) {
    const getLanguageValue = (dataArray, lang = language) => {
        if (!Array.isArray(dataArray)) return dataArray || "";

        // Try to find exact language match
        const langEntry = dataArray.find(item => item.language === lang);
        if (langEntry) return langEntry.value || "";

        // Fallback to English if specified language not found
        if (lang !== "en") {
            const enEntry = dataArray.find(item => item.language === "en");
            if (enEntry) return enEntry.value || "";
        }

        // Fallback to first available non-"dc" entry if neither found
        if (dataArray.length > 0) {
            const nonDcEntry = dataArray.find(item => item.language !== "dc" && item.value);
            if (nonDcEntry) return nonDcEntry.value;
        }

        return "";
    };

    const config = _.cloneDeep(assistantInstructionsConfig);

    if (config.postBody?.instructions) {
        config.postBody.instructions = getLanguageValue(supportBotInstructions);
    }

    if (config.supportBotInstructionBody) {
        config.supportBotInstructionBody = getLanguageValue(supportBotInstructionBody);
    }

    if (config.supportTopicInstructionBody) {
        config.supportTopicInstructionBody = getLanguageValue(supportTopicInstructionBody);
    }

    return config;
}

export const fetchAssistantToolsAndResources = async (supportBotIds, supportBots, auth) => {
    const combinedTools = [];
    const combinedToolResources = {};

    if (!Array.isArray(supportBotIds) || !supportBots) {
        return { tools: combinedTools, tool_resources: combinedToolResources };
    }

    // Collect all assistant IDs from support bots (assistantid is at root level)
    const assistantIds = supportBotIds
        .map(botId => {
            const bot = supportBots.find(b => b.id === botId);
            return bot?.assistantid || null;
        })
        .filter(id => id !== null);

    try {
        const assistantPromises = assistantIds.map(async (asstId) => {
            try {
                const { data } = await axios.get(`/api/v2/assistant.php/${asstId}`, auth);
                return data;
            } catch (error) {
                console.error(`Failed to fetch assistant ${asstId}:`, error);
                return null;
            }
        });

        const assistantDataArray = await Promise.all(assistantPromises);

        assistantDataArray.forEach(assistantData => {
            if (assistantData) {
                // Combine tools (avoiding duplicates based on type)
                if (Array.isArray(assistantData.tools)) {
                    assistantData.tools.forEach(tool => {
                        const existingTool = combinedTools.find(t => t.type === tool.type);
                        if (!existingTool) {
                            combinedTools.push(tool);
                        }
                    });
                }

                // Combine tool_resources
                if (assistantData.tool_resources) {
                    // Handle file_search resources
                    /* OpenAI assistant api supports only one vector store
                    if (assistantData.tool_resources.file_search?.vector_store_ids) {
                        if (!combinedToolResources.file_search) {
                            combinedToolResources.file_search = { vector_store_ids: [] };
                        }
                        assistantData.tool_resources.file_search.vector_store_ids.forEach(id => {
                            if (!combinedToolResources.file_search.vector_store_ids.includes(id)) {
                                combinedToolResources.file_search.vector_store_ids.push(id);
                            }
                        });
                    }
                     */
                    if (assistantData.tool_resources.file_search?.vector_store_ids) {
                        if (!combinedToolResources.file_search) {
                            combinedToolResources.file_search = {
                                vector_store_ids: [assistantData.tool_resources.file_search.vector_store_ids[0]]
                            };
                        }
                        // Skip adding more vector stores - OpenAI only supports one
                    }

                    // Handle code_interpreter resources
                    if (assistantData.tool_resources.code_interpreter?.file_ids) {
                        if (!combinedToolResources.code_interpreter) {
                            combinedToolResources.code_interpreter = { file_ids: [] };
                        }
                        assistantData.tool_resources.code_interpreter.file_ids.forEach(id => {
                            if (!combinedToolResources.code_interpreter.file_ids.includes(id)) {
                                combinedToolResources.code_interpreter.file_ids.push(id);
                            }
                        });
                    }
                }
            }
        });
    } catch (error) {
        console.error('Error fetching assistant tools and resources:', error);
    }

    return {
        tools: combinedTools.length > 0 ? combinedTools : undefined,
        tool_resources: Object.keys(combinedToolResources).length > 0 ? combinedToolResources : undefined
    };
};

export const useAssistantOperations = () => {
    const { value: assistantInstructionsConfig } = useDataContext('assistantInstructionsConfig');
    const { value: supportBots } = useDataContext('supportBots');
    const { value: categoryData } = useDataContext('categoryData');
    const { value: supportBotInstructions } = useDataContext('supportBotInstructions');
    const { value: supportTopicInstructionBody } = useDataContext('supportTopicInstructionBody');
    const { value: supportBotInstructionBody } = useDataContext('supportBotInstructionBody');
    const { instanceLang } = useLanguageData();
    const intl = useIntl();

    const buildAssistantInstructions = (surveyData, surveyId, isTest = false) => {
        const moduleInstructionsLang = surveyData?.config?.language || instanceLang || "en";

        return processAssistantInstructions({
            assistantInstructionsConfig,
            surveyData,
            supportBots,
            categoryData,
            supportBotInstructions,
            supportTopicInstructionBody,
            supportBotInstructionBody,
            moduleInstructionsLang,
            intl,
            surveyId,
            isTest
        });
    };

    const fetchCombinedToolsAndResources = async (supportBotIds) => {
        return fetchAssistantToolsAndResources(supportBotIds, supportBots, auth);
    };

    return {
        buildAssistantInstructions,
        fetchCombinedToolsAndResources
    };
};